import { useEffect, useMemo, useState } from 'react';
import { Download, Loader2, Search } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/new-components/ui/card';
import { Button } from '../components/new-components/ui/button';
import { Input } from '../components/new-components/ui/input';
import { Badge } from '../components/new-components/ui/badge';
import { PortalContainer } from '../components/portal/PortalContainer';

interface MerchantTransaction {
  id: string;
  amount: number;
  status: string;
  initiated_at: string;
  expected_settlement_date: string | null;
  actual_settlement_date: string | null;
  confirmation_code: string | null;
  institution_name: string | null;
  account_type: string | null;
  last4: string | null;
}

type StatusFilter = 'all' | 'pending' | 'processing' | 'settled' | 'returned' | 'failed';

const statusOptions: StatusFilter[] = ['all', 'pending', 'processing', 'settled', 'returned', 'failed'];

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);

const formatDateTime = (value: string | null) => {
  if (!value) return '—';
  return new Date(value).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const normalizeStatus = (status: string) => (status || 'pending').toLowerCase();

const isReturnedStatus = (status: string) => {
  const normalized = normalizeStatus(status);
  return normalized === 'returned' || normalized === 'refunded' || normalized === 'failed';
};

const isSettledStatus = (status: string) => {
  const normalized = normalizeStatus(status);
  return normalized === 'settled' || normalized === 'succeeded';
};

const getReconciliationLabel = (transaction: MerchantTransaction) => {
  if (isSettledStatus(transaction.status) && transaction.actual_settlement_date) return 'Reconciled';
  if (isReturnedStatus(transaction.status)) return 'Exception';
  return 'Open';
};

const getRemittanceLabel = (status: string) => {
  const normalized = normalizeStatus(status);
  if (isSettledStatus(normalized)) return 'Remitted';
  if (normalized === 'processing') return 'In transit';
  if (isReturnedStatus(normalized)) return 'Returned';
  return 'Pending';
};

const badgeStyle = (value: string) => {
  const normalized = value.toLowerCase();
  if (normalized === 'reconciled' || normalized === 'remitted') {
    return 'bg-[hsl(var(--success))]/10 text-[hsl(var(--success))] border-transparent';
  }
  if (normalized === 'exception' || normalized === 'returned') {
    return 'bg-destructive/10 text-destructive border-transparent';
  }
  if (normalized === 'in transit') {
    return 'bg-primary/10 text-primary border-transparent';
  }
  return 'bg-muted text-muted-foreground border-transparent';
};

const Transactions = () => {
  const [transactions, setTransactions] = useState<MerchantTransaction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    let ignore = false;
    const fetchTransactions = async () => {
      try {
        const token = localStorage.getItem('authToken');
        const response = await fetch('/api/v1/hivefund/transactions', {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const data = await response.json();
        if (ignore) return;
        setTransactions(data.transactions || []);
      } catch (error) {
        if (!ignore) console.error('Failed to load transactions:', error);
      } finally {
        if (!ignore) setIsLoading(false);
      }
    };

    fetchTransactions();
    return () => { ignore = true; };
  }, []);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();

    return transactions.filter((transaction) => {
      const normalized = normalizeStatus(transaction.status);
      const matchesStatus = statusFilter === 'all' || normalized === statusFilter;

      const matchesSearch =
        query.length === 0 ||
        transaction.id?.toLowerCase().includes(query) ||
        transaction.confirmation_code?.toLowerCase().includes(query) ||
        transaction.institution_name?.toLowerCase().includes(query) ||
        transaction.last4?.toLowerCase().includes(query);

      return matchesStatus && matchesSearch;
    });
  }, [transactions, statusFilter, search]);

  const summary = useMemo(() => {
    const grossReceipts = filtered.reduce((sum, transaction) => sum + Number(transaction.amount || 0) / 100, 0);
    const contraRevenue = filtered.reduce((sum, transaction) => {
      const gross = Number(transaction.amount || 0) / 100;
      return sum + (isReturnedStatus(transaction.status) ? gross : 0);
    }, 0);
    const settledCash = filtered.reduce((sum, transaction) => {
      const gross = Number(transaction.amount || 0) / 100;
      return sum + (isSettledStatus(transaction.status) && transaction.actual_settlement_date ? gross : 0);
    }, 0);
    const outstandingReceivable = filtered.reduce((sum, transaction) => {
      const gross = Number(transaction.amount || 0) / 100;
      const normalized = normalizeStatus(transaction.status);
      return sum + (normalized === 'pending' || normalized === 'processing' ? gross : 0);
    }, 0);

    return {
      grossReceipts,
      contraRevenue,
      netRevenue: grossReceipts - contraRevenue,
      settledCash,
      outstandingReceivable,
    };
  }, [filtered]);

  const exportCsv = () => {
    const header = [
      'Transaction ID',
      'Initiated At',
      'Settlement Date',
      'Confirmation Code',
      'Bank Account',
      'Status',
      'Reconciliation',
      'Remittance',
      'Gross Amount',
      'Contra Revenue',
      'Net Revenue',
    ];

    const rows = filtered.map((transaction) => {
      const gross = Number(transaction.amount || 0) / 100;
      const contra = isReturnedStatus(transaction.status) ? gross : 0;
      const net = gross - contra;

      return [
        transaction.id,
        transaction.initiated_at,
        transaction.actual_settlement_date || transaction.expected_settlement_date || '',
        transaction.confirmation_code || '',
        `${transaction.institution_name || 'Bank'} ••${transaction.last4 || '----'}`,
        transaction.status,
        getReconciliationLabel(transaction),
        getRemittanceLabel(transaction.status),
        gross.toFixed(2),
        contra.toFixed(2),
        net.toFixed(2),
      ];
    });

    const csv = [header, ...rows]
      .map((line) => line.map((cell) => `"${String(cell ?? '').replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `merchant-transaction-report-${Date.now()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (isLoading) {
    return (
      <PortalContainer>
        <div className="flex items-center justify-center min-h-[45vh]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </PortalContainer>
    );
  }

  return (
    <PortalContainer className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-display font-semibold text-foreground">Transactions</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Merchant reconciliation and remittance report aligned to GAAP presentation.
          </p>
        </div>
        <Button variant="outline" onClick={exportCsv}>
          <Download className="w-4 h-4" />
          Export report
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">Gross receipts</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-semibold text-foreground">{formatCurrency(summary.grossReceipts)}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">Contra revenue</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-semibold text-destructive">{formatCurrency(summary.contraRevenue)}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">Net revenue</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-semibold text-foreground">{formatCurrency(summary.netRevenue)}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">Settled cash</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-semibold text-[hsl(var(--success))]">{formatCurrency(summary.settledCash)}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">Outstanding ACH</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-semibold text-primary">{formatCurrency(summary.outstandingReceivable)}</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="pt-6 space-y-4">
          <div className="flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between">
            <div className="relative w-full lg:max-w-sm">
              <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search by ID, confirmation, bank, last4"
                className="pl-9"
              />
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              {statusOptions.map((status) => (
                <Button
                  key={status}
                  size="sm"
                  variant={statusFilter === status ? 'default' : 'outline'}
                  onClick={() => setStatusFilter(status)}
                >
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </Button>
              ))}
            </div>
          </div>

          {/* Desktop: the full reconciliation table (scrolls within its own container). */}
          <div className="hidden md:block overflow-x-auto border rounded-xl">
            <table className="w-full text-sm">
              <thead className="bg-muted/40">
                <tr className="text-left text-muted-foreground">
                  <th className="px-4 py-3 font-medium">Initiated</th>
                  <th className="px-4 py-3 font-medium">Reference</th>
                  <th className="px-4 py-3 font-medium">Account</th>
                  <th className="px-4 py-3 font-medium">Gross</th>
                  <th className="px-4 py-3 font-medium">Contra</th>
                  <th className="px-4 py-3 font-medium">Net</th>
                  <th className="px-4 py-3 font-medium">Settlement</th>
                  <th className="px-4 py-3 font-medium">Reconciliation</th>
                  <th className="px-4 py-3 font-medium">Remittance</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-4 py-10 text-center text-muted-foreground">
                      No transactions matched your filters.
                    </td>
                  </tr>
                ) : (
                  filtered.map((transaction) => {
                    const gross = Number(transaction.amount || 0) / 100;
                    const contra = isReturnedStatus(transaction.status) ? gross : 0;
                    const net = gross - contra;
                    const reconciliation = getReconciliationLabel(transaction);
                    const remittance = getRemittanceLabel(transaction.status);

                    return (
                      <tr key={transaction.id} className="border-t hover:bg-muted/20">
                        <td className="px-4 py-3 text-foreground">{formatDateTime(transaction.initiated_at)}</td>
                        <td className="px-4 py-3">
                          <p className="font-medium text-foreground">{transaction.id}</p>
                          <p className="text-xs text-muted-foreground">{transaction.confirmation_code || 'No confirmation code'}</p>
                        </td>
                        <td className="px-4 py-3 text-foreground">
                          <p>{transaction.institution_name || 'Bank account'}</p>
                          <p className="text-xs text-muted-foreground">
                            {transaction.account_type || 'account'} ••{transaction.last4 || '----'}
                          </p>
                        </td>
                        <td className="px-4 py-3 text-foreground">{formatCurrency(gross)}</td>
                        <td className="px-4 py-3 text-destructive">{formatCurrency(contra)}</td>
                        <td className="px-4 py-3 text-foreground">{formatCurrency(net)}</td>
                        <td className="px-4 py-3 text-foreground">
                          {formatDateTime(transaction.actual_settlement_date || transaction.expected_settlement_date)}
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant="outline" className={badgeStyle(reconciliation)}>
                            {reconciliation}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant="outline" className={badgeStyle(remittance)}>
                            {remittance}
                          </Badge>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile: the 9-column table is unreadable on a phone, so present each row as a card. */}
          <div className="md:hidden space-y-3">
            {filtered.length === 0 ? (
              <div className="border rounded-xl px-4 py-10 text-center text-sm text-muted-foreground">
                No transactions matched your filters.
              </div>
            ) : (
              filtered.map((transaction) => {
                const gross = Number(transaction.amount || 0) / 100;
                const contra = isReturnedStatus(transaction.status) ? gross : 0;
                const net = gross - contra;
                const reconciliation = getReconciliationLabel(transaction);
                const remittance = getRemittanceLabel(transaction.status);

                return (
                  <div key={transaction.id} className="border rounded-xl p-4 space-y-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-medium text-foreground break-all">{transaction.id}</p>
                        <p className="text-xs text-muted-foreground">{transaction.confirmation_code || 'No confirmation code'}</p>
                      </div>
                      <p className="shrink-0 text-base font-semibold text-foreground">{formatCurrency(gross)}</p>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <Badge variant="outline" className={badgeStyle(reconciliation)}>{reconciliation}</Badge>
                      <Badge variant="outline" className={badgeStyle(remittance)}>{remittance}</Badge>
                    </div>

                    <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                      <div className="min-w-0">
                        <dt className="text-xs text-muted-foreground">Account</dt>
                        <dd className="truncate text-foreground">
                          {transaction.institution_name || 'Bank account'} ••{transaction.last4 || '----'}
                        </dd>
                      </div>
                      <div className="min-w-0">
                        <dt className="text-xs text-muted-foreground">Initiated</dt>
                        <dd className="text-foreground">{formatDateTime(transaction.initiated_at)}</dd>
                      </div>
                      <div className="min-w-0">
                        <dt className="text-xs text-muted-foreground">Settlement</dt>
                        <dd className="text-foreground">
                          {formatDateTime(transaction.actual_settlement_date || transaction.expected_settlement_date)}
                        </dd>
                      </div>
                      {contra > 0 && (
                        <div className="min-w-0">
                          <dt className="text-xs text-muted-foreground">Contra / Net</dt>
                          <dd className="text-foreground">
                            <span className="text-destructive">{formatCurrency(contra)}</span> / {formatCurrency(net)}
                          </dd>
                        </div>
                      )}
                    </dl>
                  </div>
                );
              })
            )}
          </div>

          <p className="text-xs text-muted-foreground">
            Report basis: gross cash receipts, contra-revenue for returns/failures, and net revenue summary for merchant reconciliation.
          </p>
        </CardContent>
      </Card>
    </PortalContainer>
  );
};

export default Transactions;
